import { useRecoilState, useRecoilValue } from 'recoil';
import { SearchableSelect } from 'components/core';
import { builderAnswersState, builderEffectiveOutputState, visualizationsState } from 'states';
import { isDataComplete } from 'helpers/kpiBuilder/engine';
import { BuilderSection } from './BuilderSection';
import { ChartTypeStep } from './ChartTypeStep';
import { ExtraSettingsStep } from './ExtraSettingsStep';
import { GeneratedJson } from './GeneratedJson';

const AUTO_KEYS = ['aggregationPaths', 'categoryLabel', 'valueType', 'isDecimal', 'interval', 'isCumulative', 'stateName', 'plotLabel', 'resultType', 'documentType'];
const NONE = 'None';
const GAP_DRILL = 'List of these learners (created automatically)';

const show = (v: unknown): string => (Array.isArray(v) ? v.join(', ') : String(v));

// Additional Config tab in builder mode: chart type and drill-down are chosen, everything else is filled in.
export const BuilderConfigTab = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const output = useRecoilValue(builderEffectiveOutputState);
  const visualizations = useRecoilValue(visualizationsState);

  if (!isDataComplete(a) || !output) {
    return <div className="col-sm-12 col-lg-8 mt-4 alert alert-light">Finish the Data tab first. The chart types offered here depend on the question you chose.</div>;
  }

  const detailCodes = visualizations.map((v) => v.visualizationCode).filter((c) => c.endsWith('Detail'));
  const config = output.config;
  const rows = AUTO_KEYS.filter((k) => config[k] !== undefined && config[k] !== '');

  return (
    <div className="col-sm-12 col-md-12 col-lg-8 mt-4 mb-3 pb-5">
      <ChartTypeStep />

      <BuilderSection title="Number format" hint="Chosen for you from the data. Averages show decimals; counts are whole numbers.">
        <div>{config.isDecimal ? 'Decimal number' : 'Whole number'}</div>
      </BuilderSection>

      {a.recipe !== 'funnel' && (
      <BuilderSection title="When someone clicks the chart, open" hint="Optional. Choose a list visualization to open when someone clicks a bar or number, so they can see who is behind it. Leave as None for no click-through.">
        {a.recipe === 'gap' ? (
          <div>{GAP_DRILL}</div>
        ) : (
          <SearchableSelect options={[NONE, ...detailCodes]} selected={a.drill === 'none' ? NONE : a.drill} onChange={(v) => setA({ ...a, drill: v === NONE ? 'none' : v })} />
        )}
      </BuilderSection>
      )}

      <ExtraSettingsStep />

      <BuilderSection title="Set automatically" hint="These must match the query exactly, so they are filled in from the Data tab instead of being typed.">
        {rows.map((k) => (
          <div className="row small border-bottom py-1" key={k}>
            <code className="col-5">{k}</code>
            <span className="col-7">{show(config[k])}</span>
          </div>
        ))}
      </BuilderSection>

      <GeneratedJson kind="config" />
    </div>
  );
};
